import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type DocumentDetail, type Group, type GroupMember } from '../api/client';
import { BarraDeFiltros, Buscador, LimpiarFiltros, Segmentado } from '../components/Filtros';
import { AvisoError } from '../components/Aviso';
import { ApiError } from '../api/client';
import { Modal } from '../components/Modal';
import { BuscadorDePersona } from '../components/BuscadorDePersona';
import { useSession } from '../session/SessionContext';
import { enArbol, ramaDe, rutaDe } from '../grupos/arbol';
import { AccionIcono, IconoConNota } from '../components/AccionIcono';
import {
  IconoBorrar,
  IconoCandado,
  IconoDesactivar,
  IconoDocumentos,
  IconoEditar,
  IconoGrupos,
  IconoVer,
} from '../components/Iconos';

/**
 * Grupos: quién ve qué.
 *
 * La clave no aparece por ningún lado. Existe en la base y en la bitácora
 * —donde sirve para que un registro se lea solo— pero pedírsela a una persona
 * era un campo más que llenar mal. El servidor la deriva del nombre.
 *
 * Desactivar no es borrar, y el texto lo dice: un grupo que se usó para dar
 * acceso tiene que seguir existiendo o la bitácora deja de ser legible.
 */
/** Compara sin acentos ni mayúsculas: quien escribe «credito» busca «Crédito». */
const coincide = (texto: string, busca: string) =>
  texto
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .includes(
      busca
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase(),
    );

export function Grupos({ grupos, onCambio }: { grupos: Group[]; onCambio: () => void }) {
  const { t } = useTranslation();
  const { puede } = useSession();
  const administra = puede('group.manage');

  const [editando, setEditando] = useState<Group | 'nuevo' | null>(null);
  const [integrantesDe, setIntegrantesDe] = useState<Group | null>(null);
  const [confirmando, setConfirmando] = useState<Group | null>(null);
  const [documentosDe, setDocumentosDe] = useState<Group | null>(null);
  const [busca, setBusca] = useState('');
  const [verEstado, setVerEstado] = useState('');

  // En orden de árbol: cada grupo con su rama debajo (ADR-0027).
  const visibles = enArbol(
    grupos.filter(
      (g) =>
        (!busca || coincide(g.name, busca)) &&
        (!verEstado || (verEstado === 'activos') === g.isActive),
    ),
  );

  return (
    <>
      <div className="pagina__encabezado">
        <h1 className="pagina__titulo">{t('groups.title')}</h1>
        <p className="pagina__bajada">{t('groups.explanation')}</p>
      </div>

      <section className="tarjeta">
        <div className="tarjeta__encabezado">
          <h2 className="tarjeta__titulo">{t('groups.listTitle')}</h2>
          <span className="tarjeta__nota">{t('groups.count', { count: grupos.length })}</span>
          {/* Los grupos con su gente, en Excel: una hoja con los grupos y otra con
              un renglón por persona y grupo, para poder filtrar «¿en qué grupos
              está Fulana?». */}
          {administra && (
            <a className="boton boton--fantasma" href={api.exportGroupsUrl()}>
              {t('groups.exportAction')}
            </a>
          )}
          {administra && (
            <button className="boton" type="button" onClick={() => setEditando('nuevo')}>
              {t('groups.newAction')}
            </button>
          )}
        </div>

        <BarraDeFiltros>
          <Buscador id="buscarGrupo" etiqueta={t('groups.search')} valor={busca} onCambio={setBusca} />
          <Segmentado
            etiqueta={t('groups.state')}
            valor={verEstado}
            onCambio={setVerEstado}
            opciones={[
              { valor: '', etiqueta: t('users.allStatuses'), conteo: grupos.length },
              { valor: 'activos', etiqueta: t('groups.active'), conteo: grupos.filter((g) => g.isActive).length },
              { valor: 'inactivos', etiqueta: t('groups.inactive'), conteo: grupos.filter((g) => !g.isActive).length },
            ]}
          />
          <div className="barra-filtros__acciones">
            <LimpiarFiltros
              hay={Boolean(busca || verEstado)}
              onLimpiar={() => {
                setBusca('');
                setVerEstado('');
              }}
            />
          </div>
        </BarraDeFiltros>

        {visibles.length === 0 ? (
          <div className="vacio">
            <span className="vacio__titulo">
              {busca || verEstado ? t('common.noResults') : t('groups.empty')}
            </span>
            {!busca && !verEstado && <span>{t('groups.emptyHint')}</span>}
          </div>
        ) : (
          <div className="tabla-envoltura">
            <table>
              <thead>
                <tr>
                  <th>{t('groups.name')}</th>
                  <th>{t('groups.members')}</th>
                  <th title={t('groups.branchMembersHelp')}>{t('groups.branchMembers')}</th>
                  <th>{t('groups.state')}</th>
                  {administra && <th />}
                </tr>
              </thead>
              <tbody>
                {visibles.map(({ grupo, nivel }) => (
                  <tr key={grupo.id}>
                    <td>
                      <span className="grupo-arbol" style={{ paddingLeft: `${nivel * 1.25}rem` }}>
                        {nivel > 0 && <span className="grupo-arbol__rama" aria-hidden="true" />}
                        {grupo.name}
                      </span>
                      {/* Fuera del árbol —filtrando— la ruta dice de dónde cuelga. */}
                      {busca && grupo.parentId && (
                        <div className="campo__ayuda">{rutaDe(grupos, grupo.id)}</div>
                      )}
                    </td>
                    <td className="mono">{grupo.memberCount}</td>
                    <td className="mono">{grupo.branchMemberCount}</td>
                    <td>
                      <span className={`pildora pildora--${grupo.isActive ? 'bueno' : 'neutro'}`}>
                        <span className="pildora__punto" aria-hidden="true" />
                        {grupo.isActive ? t('groups.active') : t('groups.inactive')}
                      </span>
                    </td>
                    {administra && (
                      <td className="acciones-fila">
                        <AccionIcono
                          etiqueta={t('groups.documentsAction')}
                          icono={<IconoDocumentos />}
                          onClick={() => setDocumentosDe(grupo)}
                        />
                        <AccionIcono
                          etiqueta={t('groups.membersAction')}
                          icono={<IconoGrupos />}
                          onClick={() => setIntegrantesDe(grupo)}
                        />
                        <AccionIcono
                          etiqueta={t('groups.editAction')}
                          icono={<IconoEditar />}
                          onClick={() => setEditando(grupo)}
                        />
                        {/* El grupo general no se desactiva nunca. Se explica en
                            vez de dejar un botón que siempre falla: un control
                            que rebota enseña a desconfiar de todos los demás. */}
                        {grupo.isActive &&
                          (grupo.isSystem ? (
                            <IconoConNota
                              etiqueta={`${t('groups.protectedTitle')}: ${t('groups.protectedHint')}`}
                              icono={<IconoCandado />}
                            />
                          ) : (
                            <AccionIcono
                              etiqueta={t('groups.deactivateAction')}
                              icono={<IconoDesactivar />}
                              tono="peligro"
                              onClick={() => setConfirmando(grupo)}
                            />
                          ))}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="tarjeta__pie">{t('groups.footnote')}</p>
      </section>

      {editando && (
        <FormularioDeGrupo
          grupo={editando === 'nuevo' ? null : editando}
          grupos={grupos}
          onCerrar={() => setEditando(null)}
          onGuardado={() => {
            setEditando(null);
            onCambio();
          }}
        />
      )}

      {integrantesDe && (
        <Integrantes
          grupo={integrantesDe}
          onCerrar={() => setIntegrantesDe(null)}
          onCambio={onCambio}
        />
      )}

      {documentosDe && (
        <DocumentosDelGrupo
          grupo={documentosDe}
          onCerrar={() => setDocumentosDe(null)}
          onCambio={onCambio}
        />
      )}

      {confirmando && (
        <Confirmacion
          grupo={confirmando}
          onCerrar={() => setConfirmando(null)}
          onHecho={() => {
            setConfirmando(null);
            onCambio();
          }}
          // Si no se puede desactivar porque tiene documentos, el aviso deja de
          // ser un callejón: lleva a la lista donde se pueden quitar.
          onVerDocumentos={() => {
            setDocumentosDe(confirmando);
            setConfirmando(null);
          }}
        />
      )}
    </>
  );
}

function FormularioDeGrupo({
  grupo,
  grupos,
  onCerrar,
  onGuardado,
}: {
  grupo: Group | null;
  grupos: Group[];
  onCerrar: () => void;
  onGuardado: () => void;
}) {
  const { t } = useTranslation();
  const [nombre, setNombre] = useState(grupo?.name ?? '');
  const [descripcion, setDescripcion] = useState('');
  const [padre, setPadre] = useState(grupo?.parentId ?? '');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // De quién puede colgar: de cualquier grupo activo menos de su propia rama,
  // que dejaría de ser un árbol (la base lo rechaza de todos modos).
  const propia = grupo ? ramaDe(grupos, grupo.id) : new Set<string>();
  const posibles = enArbol(grupos.filter((g) => g.isActive && !propia.has(g.id)));

  const guardar = async () => {
    setGuardando(true);
    setError(null);
    try {
      if (grupo) {
        const movido = (grupo.parentId ?? '') !== padre;
        await api.updateGroup(
          grupo.id,
          nombre.trim(),
          descripcion.trim() || undefined,
          movido ? padre || null : undefined,
        );
      } else {
        await api.createGroup(nombre.trim(), descripcion.trim() || undefined, padre || undefined);
      }
      onGuardado();
    } catch (e) {
      setError(e);
      setGuardando(false);
    }
  };

  return (
    <Modal titulo={t(grupo ? 'groups.editTitle' : 'groups.createTitle')} onCerrar={onCerrar}>
      <AvisoError error={error} />

      <div className="campo">
        <label htmlFor="grupo-nombre">{t('groups.formNameLabel')}</label>
        <input
          id="grupo-nombre"
          value={nombre}
          maxLength={160}
          autoComplete="off"
          placeholder={t('groups.formNamePlaceholder')}
          onChange={(e) => setNombre(e.target.value)}
        />
      </div>

      {/* El grupo general es la raíz de todo el personal: no cuelga de nadie. */}
      {!grupo?.isSystem && (
        <div className="campo">
          <label htmlFor="grupo-padre">{t('groups.formParentLabel')}</label>
          <select id="grupo-padre" value={padre} onChange={(e) => setPadre(e.target.value)}>
            <option value="">{t('groups.formParentNone')}</option>
            {posibles.map(({ grupo: opcion, nivel }) => (
              <option key={opcion.id} value={opcion.id}>
                {'\u00a0\u00a0'.repeat(nivel)}
                {nivel > 0 ? '↳ ' : ''}
                {opcion.name}
              </option>
            ))}
          </select>
          <span className="campo__ayuda">
            {t(grupo && (grupo.parentId ?? '') !== padre ? 'groups.formParentMoveHint' : 'groups.formParentHint')}
          </span>
        </div>
      )}

      <div className="campo">
        <label htmlFor="grupo-descripcion">{t('groups.formDescriptionLabel')}</label>
        <textarea
          id="grupo-descripcion"
          value={descripcion}
          maxLength={500}
          rows={3}
          placeholder={t('groups.formDescriptionPlaceholder')}
          onChange={(e) => setDescripcion(e.target.value)}
        />
      </div>

      <div className="modal__acciones">
        <button className="boton boton--fantasma" type="button" onClick={onCerrar}>
          {t('groups.cancel')}
        </button>
        <button
          className="boton"
          type="button"
          disabled={guardando || nombre.trim().length === 0}
          onClick={guardar}
        >
          {guardando ? t('groups.saving') : t('groups.save')}
        </button>
      </div>
    </Modal>
  );
}

function Integrantes({
  grupo,
  onCerrar,
  onCambio,
}: {
  grupo: Group;
  onCerrar: () => void;
  onCambio: () => void;
}) {
  const { t } = useTranslation();
  const [miembros, setMiembros] = useState<GroupMember[] | null>(null);
  const [error, setError] = useState<unknown>(null);

  const cargar = useCallback(async () => {
    try {
      setMiembros(await api.groupMembers(grupo.id));
    } catch (e) {
      setError(e);
    }
  }, [grupo.id]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const reemplazar = async (ids: string[]) => {
    setError(null);
    try {
      setMiembros(await api.replaceGroupMembers(grupo.id, ids));
      onCambio();
    } catch (e) {
      setError(e);
    }
  };

  const actuales = (miembros ?? []).map((m) => m.userId);

  return (
    <Modal titulo={t('groups.membersTitle', { name: grupo.name })} onCerrar={onCerrar}>
      <AvisoError error={error} />

      <BuscadorDePersona
        valor=""
        etiqueta={t('groups.addMember')}
        onElegir={(userId) => {
          // Se ignora a quien ya está: agregarlo dos veces no es un error que
          // valga la pena mostrar, simplemente no cambia nada.
          if (!actuales.includes(userId)) void reemplazar([...actuales, userId]);
        }}
      />

      {miembros && miembros.length === 0 ? (
        <div className="vacio">
          <span className="vacio__titulo">{t('groups.noMembers')}</span>
          <span>{t('groups.noMembersHint')}</span>
        </div>
      ) : (
        <ul className="lista-simple">
          {(miembros ?? []).map((miembro) => (
            <li key={miembro.userId} className="lista-simple__fila">
              <span>{miembro.user?.fullName ?? t('groups.unknownPerson')}</span>
              <button
                className="boton boton--fantasma"
                type="button"
                onClick={() => void reemplazar(actuales.filter((id) => id !== miembro.userId))}
              >
                {t('groups.removeMember')}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

function Confirmacion({
  grupo,
  onCerrar,
  onHecho,
  onVerDocumentos,
}: {
  grupo: Group;
  onCerrar: () => void;
  onHecho: () => void;
  onVerDocumentos: () => void;
}) {
  const { t } = useTranslation();
  const [error, setError] = useState<unknown>(null);
  const [enCurso, setEnCurso] = useState(false);
  const tieneDocumentos = error instanceof ApiError && error.code === 'group.has_documents';

  return (
    <Modal titulo={t('groups.confirmDeactivate', { name: grupo.name })} onCerrar={onCerrar}>
      <AvisoError error={error} />
      {tieneDocumentos ? (
        <p>
          <button className="boton" type="button" onClick={onVerDocumentos}>
            {t('groups.documentsAction')}
          </button>
        </p>
      ) : (
        <p>{t('groups.confirmDeactivateHint')}</p>
      )}
      <div className="modal__acciones">
        <button className="boton boton--fantasma" type="button" onClick={onCerrar}>
          {t('groups.cancel')}
        </button>
        <button
          className="boton"
          type="button"
          disabled={enCurso}
          onClick={async () => {
            setEnCurso(true);
            setError(null);
            try {
              await api.deactivateGroup(grupo.id);
              onHecho();
            } catch (e) {
              setError(e);
              setEnCurso(false);
            }
          }}
        >
          {t('groups.confirmDeactivateAction')}
        </button>
      </div>
    </Modal>
  );
}

/**
 * Los documentos que dependen de este grupo, y cómo sacarlos.
 *
 * Existe por un callejón sin salida: el portal decía «no se puede desactivar,
 * tiene documentos» y ahí terminaba. Saber *cuáles* obligaba a ir a la pantalla
 * de documentos y filtrar a mano, y no era evidente que se pudiera.
 *
 * No permite quitar el último grupo de un documento: quedaría sin quién lo vea,
 * que es justo lo que la regla de desactivación protege. Se dice en vez de
 * dejar el botón muerto.
 */
function DocumentosDelGrupo({
  grupo,
  onCerrar,
  onCambio,
}: {
  grupo: Group;
  onCerrar: () => void;
  onCambio: () => void;
}) {
  const { t } = useTranslation();
  const [documentos, setDocumentos] = useState<DocumentDetail[] | null>(null);
  const [error, setError] = useState<unknown>(null);

  const cargar = useCallback(async () => {
    try {
      // El listado no trae los grupos de cada documento, y hacen falta para
      // saber si éste es el único —quitarlo entonces dejaría al documento sin
      // quién lo vea, y la API lo rechaza—. Se pide el detalle de cada uno: son
      // los documentos de un grupo, no el catálogo entero, y avisarlo antes es
      // mejor que dejar que el botón falle.
      const pagina = await api.documents(`?groupId=${grupo.id}&pageSize=50`);
      setDocumentos(await Promise.all(pagina.items.map((d) => api.document(d.id))));
    } catch (e) {
      setError(e);
    }
  }, [grupo.id]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const quitar = async (documento: DocumentDetail) => {
    setError(null);
    try {
      await api.updateDocumentGroups(
        documento.id,
        documento.groupIds.filter((id) => id !== grupo.id),
      );
      await cargar();
      onCambio();
    } catch (e) {
      setError(e);
    }
  };

  return (
    <Modal titulo={t('groups.documentsTitle', { name: grupo.name })} onCerrar={onCerrar}>
      <AvisoError error={error} />

      {documentos && documentos.length === 0 ? (
        <div className="vacio">
          <span className="vacio__titulo">{t('groups.noDocuments')}</span>
          <span>{t('groups.noDocumentsHint')}</span>
        </div>
      ) : (
        <ul className="lista-simple">
          {(documentos ?? []).map((documento) => {
            const esElUnico = documento.groupIds.length <= 1;
            return (
              <li key={documento.id} className="lista-simple__fila">
                <span>
                  {documento.title}
                  {esElUnico && (
                    <>
                      <br />
                      <span className="campo__ayuda">{t('groups.lastGroupWarning')}</span>
                    </>
                  )}
                </span>
                <span className="acciones-fila">
                  <AccionIcono
                    etiqueta={t('groups.openDocument')}
                    icono={<IconoVer />}
                    to={`/documentos/${documento.id}`}
                  />
                  <AccionIcono
                    etiqueta={t('groups.removeFromGroup')}
                    icono={<IconoBorrar />}
                    tono="peligro"
                    disabled={esElUnico}
                    onClick={() => void quitar(documento)}
                  />
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </Modal>
  );
}
